import type {
  FinanceDataReliability,
  FinanceTimeLogHierarchySummary,
  FinanceTimeLogReconciliationFlag,
  FinanceTimeLogReconciliationIssue,
  FinanceTimeLogReconciliationReport,
  FinanceTimeLogReconciliationSummary,
  Task,
  TimeLog,
} from "../models/production";
import type { HierarchySnapshot } from "../shared/rollupTypes";

interface ReconciliationContext {
  snapshot: HierarchySnapshot;
  timeLogs?: TimeLog[];
  now?: string;
  excludeTimeLog?: (timeLog: TimeLog) => boolean;
}

interface ResolvedMappedLog {
  timeLog: TimeLog;
  task: Task;
  artistMapped: boolean;
}

const MINUTES_PER_DAY = 480;

function roundDays(value: number) {
  return Math.round((value / MINUTES_PER_DAY) * 10) / 10;
}

function toSummaryLabel(reliability: FinanceDataReliability, summary: FinanceTimeLogReconciliationSummary) {
  if (summary.unmappedCount === 0 && summary.ambiguousCount === 0) return "Mapped cleanly";
  if (reliability === "high") return "Mostly mapped";
  if (reliability === "medium") return "Mapping gaps present";
  return "Low-confidence mapping";
}

function toHelperText(reliability: FinanceDataReliability, summary: FinanceTimeLogReconciliationSummary) {
  if (summary.totalCount === 0) {
    return "No timelogs were available to reconcile for this slice.";
  }

  if (reliability === "high") {
    return "Most timelogs are mapped to tasks cleanly, so labour cost can flow through the hierarchy with high confidence.";
  }

  if (reliability === "medium") {
    return "Some timelogs are still unmapped or ambiguous, so margin should be treated as partially assumption-driven until those links are resolved.";
  }

  return "A material share of timelog effort is not mapped cleanly, so project margin and actual labour cost should be interpreted cautiously.";
}

function summarizeFlags(summary: FinanceTimeLogReconciliationSummary): FinanceTimeLogReconciliationFlag[] {
  const flags: FinanceTimeLogReconciliationFlag[] = [];
  if (summary.unmappedCount > 0) flags.push("unmapped_timelogs_present");
  if (summary.ambiguousCount > 0) flags.push("ambiguous_timelogs_present");
  if (summary.excludedCount > 0) flags.push("excluded_timelogs_present");
  if (summary.artistUnmappedCount > 0) flags.push("artist_links_missing");
  if (summary.totalCount > 0 && summary.projectIdMismatchCount && summary.projectIdMismatchCount > 0) flags.push("project_mismatch_detected");
  return flags;
}

function summarizeReconciliation(items: {
  totalCount: number;
  totalMinutes: number;
  mappedCount: number;
  mappedMinutes: number;
  unmappedCount: number;
  unmappedMinutes: number;
  ambiguousCount: number;
  ambiguousMinutes: number;
  excludedCount: number;
  excludedMinutes: number;
  artistMappedCount: number;
  artistMappedMinutes: number;
  artistUnmappedCount: number;
  artistUnmappedMinutes: number;
  projectIdMismatchCount?: number;
}): FinanceTimeLogReconciliationSummary {
  const mappedShare = items.totalCount > 0 ? items.mappedMinutes / items.totalMinutes : 1;
  const unresolvedShare = items.totalCount > 0 ? (items.unmappedMinutes + items.ambiguousMinutes) / items.totalMinutes : 0;
  const reliability: FinanceDataReliability =
    unresolvedShare > 0.2 ? "low" : unresolvedShare > 0.05 || items.artistUnmappedCount > 0 ? "medium" : "high";
  const financialConfidenceScore = Math.max(
    0,
    Math.min(
      100,
      Math.round(
        mappedShare * 100 -
          (items.ambiguousMinutes / Math.max(items.totalMinutes, 1)) * 30 -
          (items.unmappedMinutes / Math.max(items.totalMinutes, 1)) * 50 -
          (items.artistUnmappedMinutes / Math.max(items.totalMinutes, 1)) * 10
      )
    )
  );

  const summary: FinanceTimeLogReconciliationSummary = {
    totalCount: items.totalCount,
    totalMinutes: items.totalMinutes,
    totalDays: roundDays(items.totalMinutes),
    mappedCount: items.mappedCount,
    mappedMinutes: items.mappedMinutes,
    mappedDays: roundDays(items.mappedMinutes),
    unmappedCount: items.unmappedCount,
    unmappedMinutes: items.unmappedMinutes,
    unmappedDays: roundDays(items.unmappedMinutes),
    ambiguousCount: items.ambiguousCount,
    ambiguousMinutes: items.ambiguousMinutes,
    ambiguousDays: roundDays(items.ambiguousMinutes),
    excludedCount: items.excludedCount,
    excludedMinutes: items.excludedMinutes,
    excludedDays: roundDays(items.excludedMinutes),
    artistMappedCount: items.artistMappedCount,
    artistMappedMinutes: items.artistMappedMinutes,
    artistMappedDays: roundDays(items.artistMappedMinutes),
    artistUnmappedCount: items.artistUnmappedCount,
    artistUnmappedMinutes: items.artistUnmappedMinutes,
    artistUnmappedDays: roundDays(items.artistUnmappedMinutes),
    projectIdMismatchCount: items.projectIdMismatchCount,
    financeReliability: reliability,
    financialConfidenceScore,
  };

  summary.flags = summarizeFlags(summary);
  summary.summaryLabel = toSummaryLabel(reliability, summary);
  summary.helperText = toHelperText(reliability, summary);
  return summary;
}

function buildHierarchyAccumulator() {
  return new Map<string, { mappedCount: number; mappedMinutes: number; artistMappedCount: number; artistUnmappedCount: number }>();
}

function addToHierarchySummary(
  accumulator: Map<string, { mappedCount: number; mappedMinutes: number; artistMappedCount: number; artistUnmappedCount: number }>,
  entityId: string | undefined,
  minutes: number,
  artistMapped: boolean
) {
  if (!entityId) return;
  const current = accumulator.get(entityId) || {
    mappedCount: 0,
    mappedMinutes: 0,
    artistMappedCount: 0,
    artistUnmappedCount: 0,
  };
  current.mappedCount += 1;
  current.mappedMinutes += minutes;
  if (artistMapped) current.artistMappedCount += 1;
  else current.artistUnmappedCount += 1;
  accumulator.set(entityId, current);
}

function finalizeHierarchySummary(
  accumulator: Map<string, { mappedCount: number; mappedMinutes: number; artistMappedCount: number; artistUnmappedCount: number }>
): Record<string, FinanceTimeLogHierarchySummary> {
  return Object.fromEntries(
    Array.from(accumulator.entries()).map(([entityId, entry]) => [
      entityId,
      {
        entityId,
        mappedCount: entry.mappedCount,
        mappedMinutes: entry.mappedMinutes,
        mappedDays: roundDays(entry.mappedMinutes),
        artistMappedCount: entry.artistMappedCount,
        artistUnmappedCount: entry.artistUnmappedCount,
      },
    ])
  );
}

function groupTasksByFptId(tasks: Task[]) {
  const grouped = new Map<string, Task[]>();
  for (const task of tasks) {
    if (!task.fptTaskId) continue;
    const key = String(task.fptTaskId);
    const current = grouped.get(key) || [];
    current.push(task);
    grouped.set(key, current);
  }
  return grouped;
}

function buildMappedArtistIssue(timeLog: TimeLog): FinanceTimeLogReconciliationIssue {
  return {
    id: `timelog-reconciliation:${timeLog.id}:missing-artist`,
    timeLogId: timeLog.id,
    status: "mapped",
    reasonCode: "missing_artist_link",
    summary: "Timelog is linked to a task, but the artist could not be mapped locally.",
    minutes: timeLog.minutes || 0,
    hours: timeLog.hours || 0,
    logDate: timeLog.logDate,
    projectId: timeLog.projectId,
    episodeId: timeLog.episodeId,
    sequenceId: timeLog.sequenceId,
    shotId: timeLog.shotId,
    assetId: timeLog.assetId,
    taskId: timeLog.taskId,
    taskFptId: timeLog.taskFptId,
    personId: timeLog.personId,
    fptUserId: timeLog.fptUserId,
  };
}

/**
 * Finance timelog reconciliation is intentionally separate from FPT syncing. It consumes the
 * existing hierarchy and resolved timelogs, then tells finance how much effort mapped cleanly,
 * how much remains unmapped or ambiguous, and where confidence should drop as a result.
 */
export function buildFinanceTimeLogReconciliationReport({
  snapshot,
  timeLogs = [],
  now,
  excludeTimeLog,
}: ReconciliationContext): FinanceTimeLogReconciliationReport {
  const tasks = snapshot.tasks || [];
  const tasksById = new Map(tasks.map((task) => [task.id, task] as const));
  const tasksByFptId = groupTasksByFptId(tasks);
  const taskSummaries = buildHierarchyAccumulator();
  const shotSummaries = buildHierarchyAccumulator();
  const sequenceSummaries = buildHierarchyAccumulator();
  const episodeSummaries = buildHierarchyAccumulator();
  const projectSummaries = new Map<string, ReturnType<typeof summarizeReconciliation>>();
  const artistSummaries = buildHierarchyAccumulator();
  const issues: FinanceTimeLogReconciliationIssue[] = [];
  const mappedLogs: ResolvedMappedLog[] = [];

  let totalCount = 0;
  let totalMinutes = 0;
  let mappedCount = 0;
  let mappedMinutes = 0;
  let unmappedCount = 0;
  let unmappedMinutes = 0;
  let ambiguousCount = 0;
  let ambiguousMinutes = 0;
  let excludedCount = 0;
  let excludedMinutes = 0;
  let artistMappedCount = 0;
  let artistMappedMinutes = 0;
  let artistUnmappedCount = 0;
  let artistUnmappedMinutes = 0;
  let projectIdMismatchCount = 0;
  const projectCounts = new Map<
    string,
    {
      totalCount: number;
      totalMinutes: number;
      mappedCount: number;
      mappedMinutes: number;
      unmappedCount: number;
      unmappedMinutes: number;
      ambiguousCount: number;
      ambiguousMinutes: number;
      excludedCount: number;
      excludedMinutes: number;
      artistMappedCount: number;
      artistMappedMinutes: number;
      artistUnmappedCount: number;
      artistUnmappedMinutes: number;
      projectIdMismatchCount: number;
    }
  >();

  const getProjectCounts = (projectId: string) =>
    projectCounts.get(projectId) || {
      totalCount: 0,
      totalMinutes: 0,
      mappedCount: 0,
      mappedMinutes: 0,
      unmappedCount: 0,
      unmappedMinutes: 0,
      ambiguousCount: 0,
      ambiguousMinutes: 0,
      excludedCount: 0,
      excludedMinutes: 0,
      artistMappedCount: 0,
      artistMappedMinutes: 0,
      artistUnmappedCount: 0,
      artistUnmappedMinutes: 0,
      projectIdMismatchCount: 0,
    };

  for (const timeLog of timeLogs) {
    const minutes = timeLog.minutes || 0;
    totalCount += 1;
    totalMinutes += minutes;
    const projectCountsEntry = getProjectCounts(timeLog.projectId);
    projectCountsEntry.totalCount += 1;
    projectCountsEntry.totalMinutes += minutes;

    if (excludeTimeLog?.(timeLog)) {
      excludedCount += 1;
      excludedMinutes += minutes;
      projectCountsEntry.excludedCount += 1;
      projectCountsEntry.excludedMinutes += minutes;
      issues.push({
        id: `timelog-reconciliation:${timeLog.id}:excluded`,
        timeLogId: timeLog.id,
        status: "excluded",
        reasonCode: "excluded_by_rule",
        summary: "Timelog was excluded by finance business rules and is not contributing to margin reporting.",
        minutes,
        hours: timeLog.hours || 0,
        logDate: timeLog.logDate,
        projectId: timeLog.projectId,
        episodeId: timeLog.episodeId,
        sequenceId: timeLog.sequenceId,
        shotId: timeLog.shotId,
        assetId: timeLog.assetId,
        taskId: timeLog.taskId,
        taskFptId: timeLog.taskFptId,
        personId: timeLog.personId,
        fptUserId: timeLog.fptUserId,
      });
      projectCounts.set(timeLog.projectId, projectCountsEntry);
      continue;
    }

    const directTask = timeLog.taskId ? tasksById.get(timeLog.taskId) : undefined;
    const fptTaskCandidates = timeLog.taskFptId ? tasksByFptId.get(String(timeLog.taskFptId)) || [] : [];

    let resolvedTask: Task | undefined;
    if (directTask) {
      resolvedTask = directTask;
    } else if (fptTaskCandidates.length === 1) {
      resolvedTask = fptTaskCandidates[0];
    }

    if (!resolvedTask && fptTaskCandidates.length > 1) {
      ambiguousCount += 1;
      ambiguousMinutes += minutes;
      projectCountsEntry.ambiguousCount += 1;
      projectCountsEntry.ambiguousMinutes += minutes;
      issues.push({
        id: `timelog-reconciliation:${timeLog.id}:ambiguous`,
        timeLogId: timeLog.id,
        status: "ambiguous",
        reasonCode: "multiple_task_candidates",
        summary: "Timelog references an FPT task that matches multiple local tasks, so finance cannot safely assign it.",
        minutes,
        hours: timeLog.hours || 0,
        logDate: timeLog.logDate,
        projectId: timeLog.projectId,
        episodeId: timeLog.episodeId,
        sequenceId: timeLog.sequenceId,
        shotId: timeLog.shotId,
        assetId: timeLog.assetId,
        taskId: timeLog.taskId,
        taskFptId: timeLog.taskFptId,
        personId: timeLog.personId,
        fptUserId: timeLog.fptUserId,
      });
      projectCounts.set(timeLog.projectId, projectCountsEntry);
      continue;
    }

    if (!resolvedTask) {
      unmappedCount += 1;
      unmappedMinutes += minutes;
      projectCountsEntry.unmappedCount += 1;
      projectCountsEntry.unmappedMinutes += minutes;
      issues.push({
        id: `timelog-reconciliation:${timeLog.id}:unmapped`,
        timeLogId: timeLog.id,
        status: "unmapped",
        reasonCode: timeLog.taskId || timeLog.taskFptId ? "unknown_task_reference" : "missing_task_link",
        summary:
          timeLog.taskId || timeLog.taskFptId
            ? "Timelog references a task that does not exist in the local finance hierarchy."
            : "Timelog has no usable task link, so finance cannot attribute it through the hierarchy.",
        minutes,
        hours: timeLog.hours || 0,
        logDate: timeLog.logDate,
        projectId: timeLog.projectId,
        episodeId: timeLog.episodeId,
        sequenceId: timeLog.sequenceId,
        shotId: timeLog.shotId,
        assetId: timeLog.assetId,
        taskId: timeLog.taskId,
        taskFptId: timeLog.taskFptId,
        personId: timeLog.personId,
        fptUserId: timeLog.fptUserId,
      });
      projectCounts.set(timeLog.projectId, projectCountsEntry);
      continue;
    }

    mappedCount += 1;
    mappedMinutes += minutes;
    projectCountsEntry.mappedCount += 1;
    projectCountsEntry.mappedMinutes += minutes;

    const artistMapped = Boolean(timeLog.personId);
    if (artistMapped) {
      artistMappedCount += 1;
      artistMappedMinutes += minutes;
      projectCountsEntry.artistMappedCount += 1;
      projectCountsEntry.artistMappedMinutes += minutes;
    } else {
      artistUnmappedCount += 1;
      artistUnmappedMinutes += minutes;
      projectCountsEntry.artistUnmappedCount += 1;
      projectCountsEntry.artistUnmappedMinutes += minutes;
      issues.push(buildMappedArtistIssue(timeLog));
    }

    if (timeLog.projectId && resolvedTask.projectId && timeLog.projectId !== resolvedTask.projectId) {
      projectIdMismatchCount += 1;
      projectCountsEntry.projectIdMismatchCount += 1;
      issues.push({
        id: `timelog-reconciliation:${timeLog.id}:project-mismatch`,
        timeLogId: timeLog.id,
        status: "mapped",
        reasonCode: "project_mismatch",
        summary: "Timelog mapped to a task, but the stored project reference does not match the task hierarchy.",
        minutes,
        hours: timeLog.hours || 0,
        logDate: timeLog.logDate,
        projectId: timeLog.projectId,
        episodeId: timeLog.episodeId,
        sequenceId: timeLog.sequenceId,
        shotId: timeLog.shotId,
        assetId: timeLog.assetId,
        taskId: resolvedTask.id,
        taskFptId: timeLog.taskFptId || resolvedTask.fptTaskId,
        personId: timeLog.personId,
        fptUserId: timeLog.fptUserId,
      });
    }

    mappedLogs.push({ timeLog, task: resolvedTask, artistMapped });
    projectCounts.set(timeLog.projectId, projectCountsEntry);
  }

  for (const mapped of mappedLogs) {
    const { timeLog, task, artistMapped } = mapped;
    addToHierarchySummary(taskSummaries, task.id, timeLog.minutes || 0, artistMapped);
    addToHierarchySummary(shotSummaries, task.shotId, timeLog.minutes || 0, artistMapped);
    addToHierarchySummary(sequenceSummaries, task.sequenceId, timeLog.minutes || 0, artistMapped);
    addToHierarchySummary(episodeSummaries, task.episodeId, timeLog.minutes || 0, artistMapped);
    addToHierarchySummary(artistSummaries, timeLog.personId || undefined, timeLog.minutes || 0, artistMapped);
  }

  for (const [projectId, counts] of projectCounts.entries()) {
    projectSummaries.set(projectId, summarizeReconciliation(counts));
  }

  return {
    generatedAt: now || new Date().toISOString(),
    summary: summarizeReconciliation({
      totalCount,
      totalMinutes,
      mappedCount,
      mappedMinutes,
      unmappedCount,
      unmappedMinutes,
      ambiguousCount,
      ambiguousMinutes,
      excludedCount,
      excludedMinutes,
      artistMappedCount,
      artistMappedMinutes,
      artistUnmappedCount,
      artistUnmappedMinutes,
      projectIdMismatchCount,
    }),
    issues,
    taskSummaries: finalizeHierarchySummary(taskSummaries),
    shotSummaries: finalizeHierarchySummary(shotSummaries),
    sequenceSummaries: finalizeHierarchySummary(sequenceSummaries),
    episodeSummaries: finalizeHierarchySummary(episodeSummaries),
    projectSummaries: Object.fromEntries(projectSummaries.entries()),
    artistSummaries: finalizeHierarchySummary(artistSummaries),
  };
}
